import { apiJson } from './api';

/**
 * Permission yang diterima `NotificationDeliveriesService.list` di backend.
 *
 * Disalin di satu tempat agar rute dan lonceng notifikasi memakai syarat yang
 * sama. Sebelumnya keduanya berbeda, sehingga peran seperti SPMB_LEADERSHIP
 * melihat lonceng lalu ditolak ketika membuka halamannya.
 */
export const NOTIFICATION_DELIVERY_PERMISSIONS = [
  'notification.deliveries.read',
  'notification.read',
  'spmb.reporting.read',
  'spmb.applicants.read',
] as const;

export const NOTIFICATION_TEMPLATE_LABELS: Record<string, string> = {
  // Referral
  'referral.partner.account-created': 'Akun Mitra Referral Dibuat',
  'referral.partner.created': 'Pendaftaran Mitra Referral',
  'referral.partner.bank-account-changed': 'Perubahan Rekening Mitra',
  'referral.partner.suspended': 'Penonaktifan Akun Mitra',
  'referral.partner.updated': 'Pembaruan Data Mitra',

  // SPMB General & Status
  'spmb.applicant.status-changed': 'Perubahan Status Pendaftar',
  'spmb.applicant.selection-released': 'Pengumuman Hasil Seleksi',
  'spmb.applicant.created': 'Pendaftar Baru Dibuat',

  // SPMB Scheduling
  'spmb.schedule.assigned': 'Penetapan Jadwal Observasi',
  'spmb.schedule.reminder-due': 'Pengingat Jadwal Observasi',
  'spmb.schedule.reminder-due.form-pending': 'Pengingat Observasi (Formulir Belum Lengkap)',

  // Finance - Form Fee
  'finance.form-fee.invoice-issued': 'Tagihan Biaya Formulir',
  'finance.form-fee.payment-verified': 'Pembayaran Biaya Formulir Diterima',

  // Finance - Entrance Fee
  'finance.entrance-fee.invoice-issued': 'Tagihan Uang Muka',
  'finance.entrance-fee.payment-verified': 'Pembayaran Uang Muka Diterima',
  'finance.entrance-fee.refund-due': 'Pengajuan Pengembalian Uang Muka',
  'finance.entrance-fee.refund-settled': 'Pengembalian Uang Muka Selesai',

  // Finance - Re-registration
  'finance.re-registration.invoice-issued': 'Tagihan Daftar Ulang',
  'finance.re-registration.payment-verified': 'Pembayaran Daftar Ulang Diterima',

  // Legacy uppercase keys
  SPMB_REGISTRATION_SUCCESS: 'Pendaftaran Berhasil',
  SPMB_FORM_FEE_PAID: 'Pembayaran Formulir Terkonfirmasi',
  SPMB_OBSERVATION_SCHEDULED: 'Jadwal Observasi Ditentukan',
  SPMB_SELECTION_ANNOUNCED: 'Pengumuman Hasil Seleksi',
  SPMB_RE_REGISTRATION_PAID: 'Daftar Ulang Terkonfirmasi',
};

export interface NotificationDeliveryItem {
  id: string;
  createdAt: string;
  sentAt?: string | null;
  channel: 'EMAIL' | 'WHATSAPP' | string;
  template: string;
  templateName?: string | null;
  recipient: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SUPPRESSED' | string;
  attemptCount?: number;
  lastError?: string | null;
  providerMessageId?: string | null;
  outboxEventId?: string | null;
  payload?: Record<string, unknown> | null;
  renderedSubject?: string | null;
  renderedBody?: string | null;
  rawSubject?: string | null;
  rawBody?: string | null;
}

export function formatNotificationTitle(templateKey: string): string {
  if (!templateKey) return 'Notifikasi Sistem';
  if (NOTIFICATION_TEMPLATE_LABELS[templateKey]) {
    return NOTIFICATION_TEMPLATE_LABELS[templateKey];
  }

  return templateKey
    .replace(/[._-]+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface NotificationDeliveriesStats {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  suppressed: number;
}

export interface NotificationDeliveriesResponse {
  items: NotificationDeliveryItem[];
  total: number;
  limit: number;
  offset: number;
  stats: NotificationDeliveriesStats;
}

export interface GetNotificationDeliveriesParams {
  limit?: number;
  offset?: number;
  page?: number;
  applicantId?: string;
  status?: string;
  channel?: string;
  search?: string;
}

export async function getNotificationDeliveries(
  paramsOrLimit: GetNotificationDeliveriesParams | number = 50,
  offset = 0,
  applicantId?: string,
): Promise<NotificationDeliveriesResponse> {
  const params = new URLSearchParams();

  if (typeof paramsOrLimit === 'object' && paramsOrLimit !== null) {
    const {
      limit = 50,
      offset: pOffset,
      page,
      applicantId: pApplicantId,
      status,
      channel,
      search,
    } = paramsOrLimit;

    params.set('limit', String(limit));
    if (page !== undefined) params.set('page', String(page));
    else if (pOffset !== undefined) params.set('offset', String(pOffset));
    if (pApplicantId) params.set('applicantId', pApplicantId);
    if (status && status !== 'ALL') params.set('status', status);
    if (channel && channel !== 'ALL') params.set('channel', channel);
    if (search && search.trim()) params.set('search', search.trim());
  } else {
    params.set('limit', String(paramsOrLimit));
    params.set('offset', String(offset));
    if (applicantId) params.set('applicantId', applicantId);
  }

  return apiJson<NotificationDeliveriesResponse>(
    `/notification-deliveries?${params.toString()}`,
    {},
    'Gagal memuat data notifikasi',
  );
}

export async function getNotificationDelivery(id: string) {
  return apiJson<NotificationDeliveryItem>(
    `/notification-deliveries/${id}`,
    {},
    'Gagal memuat detail notifikasi',
  );
}

export async function resendNotification(id: string) {
  return apiJson(
    `/notification-deliveries/${id}/resend`,
    {
      method: 'POST',
    },
    'Gagal mengirim ulang notifikasi',
  );
}


